import { z } from 'zod'

import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import {
  indexSymbolsQueryOptions,
  signalHistoryQueryOptions,
  signalSessionsQueryOptions,
} from '@/features/signals/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import type { HistoryQuery } from '@/schemas/signals'
import {
  pagingParams,
  sessionDateParamSchema,
  symbolSearchParamSchema,
} from '@/screens/signals/search'

/**
 * - `date` — the session the picker names. The read takes none (it always serves the latest),
 *   so a past one only turns the page to its no-data state (`asks/signals-dates.md`).
 * - `q` — a symbol prefix.
 * - `page`/`pageSize` — server paging; 15 a page, as the frame draws (owner
 *   2026-10-12 put 15 on the signals pager, which settles C7).
 */
export const signalHistorySearchSchema = z.object({
  date: sessionDateParamSchema,
  q: symbolSearchParamSchema,
  ...pagingParams(),
})
export type SignalHistorySearch = z.infer<typeof signalHistorySearchSchema>

export const historyQueryOf = (search: SignalHistorySearch): HistoryQuery => ({
  q: search.q ?? null,
  page: search.page,
  pageSize: search.pageSize,
})

/**
 * The `/market/signals/history` loader. MAIN: the entry URL's page.
 * SECONDARY: the index symbols (which rows link).
 */
export async function loadSignalHistory({
  context: { queryClient },
  location,
  preload,
}: {
  context: RouterContext
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  const search = signalHistorySearchSchema.parse(location.search)
  if (!preload) {
    prefetchRouteData(queryClient, signalSessionsQueryOptions())
    prefetchRouteData(queryClient, indexSymbolsQueryOptions())
  }
  await ensureRouteData(queryClient, signalHistoryQueryOptions(historyQueryOf(search)))
}
